import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

const VIRTUAL_FROM = 100;
const OVERSCAN_PX = 800;

/**
 * List that windows rows against the page scroll once it passes 100 rows, so long menus stay
 * fast on low-end phones. Rows may have different (but known) heights — e.g. a menu mixes
 * category headers and dish rows. Up to 100 rows render plainly.
 */
export function VirtualList<T>({
  items,
  rowHeight,
  getKey,
  renderRow,
}: {
  items: readonly T[];
  /** Fixed pixel height of a row (a number for every row, or per item). */
  rowHeight: number | ((item: T) => number);
  getKey: (item: T) => string;
  renderRow: (item: T) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const virtual = items.length > VIRTUAL_FROM;

  // offsets[i] = top of row i; offsets[length] = total height.
  const offsets = useMemo(() => {
    const out = new Array<number>(items.length + 1);
    out[0] = 0;
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i] as T;
      out[i + 1] =
        (out[i] as number) + (typeof rowHeight === 'number' ? rowHeight : rowHeight(item));
    }
    return out;
  }, [items, rowHeight]);

  const [range, setRange] = useState({ start: 0, end: 40 });

  useEffect(() => {
    if (!virtual) return;
    const firstAtOrAfter = (y: number) => {
      let lo = 0;
      let hi = items.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if ((offsets[mid + 1] as number) <= y) lo = mid + 1;
        else hi = mid;
      }
      return lo;
    };
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const viewTop = Math.max(0, -top - OVERSCAN_PX);
      const viewBottom = -top + window.innerHeight + OVERSCAN_PX;
      const start = firstAtOrAfter(viewTop);
      const end = Math.min(items.length, firstAtOrAfter(viewBottom) + 1);
      setRange((current) =>
        current.start === start && current.end === end ? current : { start, end },
      );
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [virtual, items.length, offsets]);

  if (!virtual) {
    return (
      <div>
        {items.map((item) => (
          <div key={getKey(item)}>{renderRow(item)}</div>
        ))}
      </div>
    );
  }

  const visible = items.slice(range.start, range.end);
  return (
    <div ref={ref} className="relative" style={{ height: offsets[items.length] }}>
      {visible.map((item, index) => {
        const at = range.start + index;
        const top = offsets[at] as number;
        const height = (offsets[at + 1] as number) - top;
        return (
          <div key={getKey(item)} className="absolute inset-x-0" style={{ top, height }}>
            {renderRow(item)}
          </div>
        );
      })}
    </div>
  );
}
